import React from 'react';
import {
  Radio as MuiRadio,
  RadioProps as MuiRadioProps,
  Typography,
  SvgIcon,
  useTheme,
} from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';
import { Box } from '../Box';

// Unchecked icon - empty circle
const UncheckedIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1" />
  </SvgIcon>
);

// Checked icon - circle with inner dot
const CheckedIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1" />
    <circle cx="12" cy="12" r="5" fill="currentColor" />
  </SvgIcon>
);

export interface RadioProps
  extends Omit<MuiRadioProps, 'size' | keyof PounceStyleProps>, PounceStyleProps {
  /** The label associated with the Radio. Appears on the right. */
  label?: string;

  /** The size of the Radio. Defaults to `medium`. */
  size?: 'small' | 'medium';

  /** Whether the checkbox is currently disabled */
  disabled?: boolean;

  /**  Whether the input has an invalid value or not */
  invalid?: boolean;

  /** Optional styles to add to the label next to the radio */
  labelSx?: PounceStyleProps;

  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiRadioProps['sx'];
}

/**
 * Radio component built on top of MUI Radio.
 *
 * Under the hood it renders a `radio` element, so you
 * can pass it as many native attrs as you wish.
 *
 * Provides all the functionality from the original Pounce Radio while leveraging
 * MUI's accessibility and interaction patterns.
 */
export const Radio = React.forwardRef<HTMLButtonElement, RadioProps>(function Radio(
  { checked, label, disabled, invalid, hidden, labelSx, size = 'medium', readOnly, sx, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);
  const theme = useTheme();

  // Combine pounce-generated styles with user sx
  const combinedSx = {
    ...pounceStyles,
    // Handle size since MUI Radio doesn't have native size prop
    // Theme defaults to medium (16px), override for large (26px)
    ...(size === 'medium'
      ? {
          '& .MuiSvgIcon-root': {
            width: '26px',
            height: '26px',
          },
        }
      : {}),
    ...(sx || {}),
  } as MuiRadioProps['sx'];

  // Create label styles if labelSx is provided
  const labelStyles = labelSx ? mapPouncePropsToSx(labelSx) : {};

  const radioElement = (
    <MuiRadio
      ref={ref}
      checked={checked}
      disabled={disabled}
      readOnly={readOnly}
      color={invalid ? 'error' : 'primary'}
      icon={<UncheckedIcon />}
      checkedIcon={<CheckedIcon />}
      sx={combinedSx}
      slotProps={{
        input: {
          'aria-invalid': invalid,
          'aria-readonly': readOnly,
        },
      }}
      {...otherProps}
    />
  );

  return (
    <Box
      as="label"
      display="inline-flex"
      alignItems="center"
      gap={label ? 3 : undefined}
      cursor={disabled ? 'default' : 'pointer'}
      verticalAlign="top"
      aria-disabled={disabled}
      aria-hidden={hidden}
      hidden={hidden}
    >
      {radioElement}
      {label && (
        <Typography
          component="span"
          fontSize={
            size === 'medium'
              ? theme.typography['body/lg']?.fontSize
              : theme.typography['body/md']?.fontSize
          }
          fontWeight={
            size === 'medium'
              ? theme.typography['body/sm']?.fontWeight
              : theme.typography?.fontWeightMedium
          }
          sx={{
            userSelect: 'none',
            ...labelStyles,
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
});
